/**
 * Which sign-in method this person used last, so the login form opens on it (KAN-621).
 *
 * ── Why remember, rather than pick a global default ──
 *
 * "Should phone be the default?" has no single right answer, because the right default differs
 * per person. Every customer who existed before V76 has an email — `homeowners.email` was
 * `NOT NULL` until then — so defaulting the login page to phone would make the common case worse
 * for the entire existing base in order to help people who do not have accounts yet. Defaulting
 * to email leaves phone-first customers switching modes on every visit instead.
 *
 * Remembering dissolves the question: returning customers get their own method, and only a
 * genuinely new visitor sees the default.
 *
 * ── localStorage, not sessionStorage ──
 *
 * The whole point is to survive between visits, which sessionStorage does not.
 *
 * ── Sign-out deliberately does NOT clear it ──
 *
 * `signOutUser` clears the checkout draft, the booking keys and the PostHog identity, so the
 * omission here is a decision rather than an oversight. Signing out and coming back is the exact
 * case the feature exists for; forgetting then would leave it working only within a session,
 * which is what sessionStorage would already have given us. The value is "phone" or "email" --
 * no number, no address, nothing about the account -- so it survives a handover harmlessly.
 *
 * ── It must degrade to EMAIL, never to nothing ──
 *
 * Reads and writes are wrapped because `localStorage` does not merely come back empty in a
 * private window or with site data blocked — the accessor itself can throw. An unreadable value
 * has to mean "email", the safe default: email works for every account, whereas showing the phone
 * form to someone who has no phone on file is a dead end.
 */

const LAST_AUTH_METHOD_KEY = "hh:last_auth_method";

export type AuthMethod = "phone" | "email";

/**
 * Records the method that just succeeded.
 *
 * Every email-shaped door — password, magic link, Google, Microsoft, Yahoo — records "email",
 * because they all land the customer on the same email form. Only the credential matters here,
 * not which door they came through.
 */
export function rememberAuthMethod(method: AuthMethod): void {
  try {
    localStorage.setItem(LAST_AUTH_METHOD_KEY, method);
  } catch {
    // A convenience, not a requirement. Losing it costs one extra tap next visit.
  }
}

/** The remembered method, or null when there is nothing usable stored. */
export function readLastAuthMethod(): AuthMethod | null {
  try {
    const value = localStorage.getItem(LAST_AUTH_METHOD_KEY);
    return value === "phone" || value === "email" ? value : null;
  } catch {
    return null;
  }
}

/**
 * Whether the login form should open in phone mode.
 *
 * ⚠️ `phoneLoginAvailable` is required, and not merely respected — it is the trap in this
 * feature. Without it, a customer who last used phone would be shown the phone form after the
 * rollout flag was turned off, with the toggle back to email hidden by that same flag: a form
 * they cannot submit and cannot escape.
 */
export function shouldOpenInPhoneMode(phoneLoginAvailable: boolean): boolean {
  if (!phoneLoginAvailable) return false;
  return readLastAuthMethod() === "phone";
}
